/**
 * dawhub landing page (route `/`).
 *
 * Marketing-only; no engine imports. The viewer lives at `/diff`
 * and `#/diff/{slug}` — see main.ts for route dispatch. CLI / terminal
 * content lives at `/flpdiff` (see flpdiff-page.ts).
 */

import { h } from "./ui.ts";
import { initHeroFx } from "./hero-fx.ts";
import { renderNav, renderFooter, renderWaitlistForm } from "./page-shared.ts";

export function initLandingFx(root: HTMLElement): void {
  const canvas = root.querySelector(".hero-fx");
  if (canvas instanceof HTMLCanvasElement) initHeroFx(canvas);
}

export function renderLanding(): HTMLElement {
  return h("div", { class: "landing" }, [
    renderNav("landing"),
    renderHero(),
    renderProblem(),
    renderAiSection(),
    renderVcSection(),
    renderUseCases(),
    renderRoadmap(),
    renderSocialProof(),
    renderFinalCta(),
    renderFaq(),
    renderFooter(),
  ]);
}

// ---- hero ----------------------------------------------------------

function renderHero(): HTMLElement {
  return h("section", { class: "hero hero--landing" }, [
    h("canvas", { class: "hero-fx", "aria-hidden": "true" }),
    h("div", { class: "container" }, [
      h("div", { class: "hero-stack" }, [
        h("span", { class: "hero-eyebrow" }, "PRIVATE BETA · COMING Q3 2026"),
        h("h1", { class: "hero-title hero-title--landing" }, [
          h("span", { class: "line" }, "AI-NATIVE"),
          h("span", { class: "line" }, [
            h("span", { class: "underline" }, "VERSION CONTROL"),
          ]),
          h("span", { class: "line" }, "FOR FL STUDIO."),
        ]),
        h("p", { class: "hero-sub" }, [
          "Track every save. ",
          h("span", { class: "accent-text" }, "Talk to your project."),
          " Roll back any change — mixer, pattern, or plugin — without losing the rest.",
        ]),
        h("div", { class: "hero-form" }, renderWaitlistForm({ ariaLabelPrefix: "Hero" })),
        h("p", { class: "hero-note" }, [
          "We email when there's something to share. ",
          h("a", { href: "/flpdiff", class: "hero-link" }, "Try our free CLI, flpdiff →"),
        ]),
      ]),
      renderHeroDiagram(),
    ]),
  ]);
}

/**
 * Abstract "save → compare → collaborate" diagram. Three stacked
 * frames connected by accent lines. Monochrome with a single accent
 * highlight per state. No real UI — concept only.
 */
function renderHeroDiagram(): HTMLElement {
  const svg = h("div", { class: "hero-diagram", "aria-hidden": "true" });
  svg.innerHTML = `
    <svg viewBox="0 0 320 320" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
      <defs>
        <pattern id="grid" width="16" height="16" patternUnits="userSpaceOnUse">
          <path d="M 16 0 L 0 0 0 16" fill="none" stroke="currentColor" stroke-width="0.5" opacity="0.18"/>
        </pattern>
      </defs>
      <rect width="320" height="320" fill="url(#grid)"/>

      <!-- save -->
      <g transform="translate(40, 40)">
        <rect width="80" height="50" fill="#faf7ee" stroke="#0a0a0a" stroke-width="2"/>
        <rect x="6" y="8"  width="46" height="3" fill="#0a0a0a"/>
        <rect x="6" y="16" width="60" height="3" fill="#0a0a0a"/>
        <rect x="6" y="24" width="36" height="3" fill="#0a0a0a"/>
        <rect x="6" y="32" width="54" height="3" fill="#ff3d14"/>
        <text x="40" y="68" font-family="JetBrains Mono, monospace" font-size="9" text-anchor="middle" fill="#0a0a0a" letter-spacing="0.1em">SAVE</text>
      </g>

      <!-- compare -->
      <g transform="translate(200, 130)">
        <rect width="80" height="50" fill="#faf7ee" stroke="#0a0a0a" stroke-width="2"/>
        <line x1="40" y1="6" x2="40" y2="44" stroke="#0a0a0a" stroke-width="1.5"/>
        <rect x="6"  y="10" width="28" height="3" fill="#1a7a1a"/>
        <rect x="6"  y="18" width="20" height="3" fill="#0a0a0a"/>
        <rect x="6"  y="26" width="24" height="3" fill="#ff3d14"/>
        <rect x="46" y="10" width="20" height="3" fill="#1a7a1a"/>
        <rect x="46" y="18" width="28" height="3" fill="#0a0a0a"/>
        <rect x="46" y="26" width="14" height="3" fill="#0a0a0a"/>
        <text x="40" y="68" font-family="JetBrains Mono, monospace" font-size="9" text-anchor="middle" fill="#0a0a0a" letter-spacing="0.1em">COMPARE</text>
      </g>

      <!-- collaborate -->
      <g transform="translate(60, 220)">
        <rect width="80" height="50" fill="#faf7ee" stroke="#0a0a0a" stroke-width="2"/>
        <circle cx="20" cy="25" r="8" fill="#ff3d14" stroke="#0a0a0a" stroke-width="1.5"/>
        <circle cx="60" cy="25" r="8" fill="#0a0a0a"/>
        <line x1="28" y1="25" x2="52" y2="25" stroke="#0a0a0a" stroke-width="2"/>
        <text x="40" y="68" font-family="JetBrains Mono, monospace" font-size="9" text-anchor="middle" fill="#0a0a0a" letter-spacing="0.1em">COLLABORATE</text>
      </g>

      <!-- connecting arrows -->
      <path d="M 120 65 Q 170 65 200 130" fill="none" stroke="#0a0a0a" stroke-width="1.5" stroke-dasharray="3 3"/>
      <path d="M 240 180 Q 240 220 140 245" fill="none" stroke="#0a0a0a" stroke-width="1.5" stroke-dasharray="3 3"/>
    </svg>
  `;
  return svg;
}

// ---- problem -------------------------------------------------------

function renderProblem(): HTMLElement {
  return h("section", { class: "section--problem", id: "problem" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "THE PROBLEM"),
        h("h2", { class: "section-title" }, [
          "WHAT DEVS HAVE, ",
          h("span", { class: "underline" }, "PRODUCERS DON'T"),
          ".",
        ]),
      ]),
      h("div", { class: "problem-grid" }, [
        h("div", { class: "problem-copy" }, [
          h("p", null, [
            "Software developers have ",
            h("code", null, "git"),
            ", branches, diffs, and AI assistants that understand their code. Music producers have ",
            h("code", null, "track_v1.flp"),
            ", ",
            h("code", null, "track_v2_FINAL.flp"),
            ", and a Discord channel.",
          ]),
          h("p", null, "The gap isn't a technology problem. Nobody built proper tooling for the way producers actually work — exploratory, iterative, often collaborative."),
          h("p", null, [
            h("strong", null, "dawhub closes that gap."),
          ]),
        ]),
        h("div", { class: "problem-compare" }, [
          h("div", { class: "problem-side problem-side--dev" }, [
            h("div", { class: "problem-side__tag" }, "DEVELOPERS"),
            h("div", { class: "problem-list" }, [
              h("div", null, "git log"),
              h("div", null, "git diff"),
              h("div", null, "pull requests"),
              h("div", null, "code review"),
              h("div", { class: "ok" }, "Claude / Copilot"),
            ]),
          ]),
          h("div", { class: "problem-side problem-side--prod" }, [
            h("div", { class: "problem-side__tag" }, "PRODUCERS"),
            h("div", { class: "problem-list" }, [
              h("div", { class: "dim" }, "track_v1.flp"),
              h("div", { class: "dim" }, "track_v2.flp"),
              h("div", { class: "dim" }, "track_v2_FINAL.flp"),
              h("div", { class: "dim" }, "track_v2_FINAL_real.flp"),
              h("div", { class: "warn" }, "what changed??"),
            ]),
          ]),
        ]),
      ]),
    ]),
  );
}

// ---- AI section ----------------------------------------------------

interface AiExample {
  user: string;
  ai: (Node | string)[];
  outcome: string;
}

const AI_EXAMPLES: AiExample[] = [
  {
    user: "What changed in this project since Tuesday?",
    ai: [
      "5 saves between Tue and now. Biggest changes: new ",
      h("strong", null, "Pad Synth"),
      " channel (Serum), Lead volume +7%, and Pattern 7 bass redone — 1 note removed, 3 shifted.",
    ],
    outcome: "AI grounded in your real diff history",
  },
  {
    user: "Find the version where the breakdown actually worked.",
    ai: [
      h("strong", null, "track_v8.flp"),
      " — Pattern 12 had the 4-bar rest before the drop. v9 removed it; v10–v12 added it back differently. Want me to roll Pattern 12 to v8?",
    ],
    outcome: "Search across every save you ever made",
  },
  {
    user: "Suggest a kick replacement that fits the genre.",
    ai: [
      "Three options based on the existing kit: ",
      h("strong", null, "707 layered with sub"),
      ", ",
      h("strong", null, "punchy 909 with tail"),
      ", or ",
      h("strong", null, "distorted analog kick"),
      ". Want me to drop one in and show the diff?",
    ],
    outcome: "Edits proposed as diffs you can review",
  },
];

function renderAiSection(): HTMLElement {
  return h("section", { class: "section--ai", id: "ai" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "AI INTEGRATION"),
        h("h2", { class: "section-title" }, [
          "LET CLAUDE WORK ON ",
          h("span", { class: "underline" }, "YOUR PROJECTS"),
          ".",
        ]),
      ]),
      h("p", { class: "section-lead" }, "Connect dawhub to Claude or any MCP-compatible AI and have a conversation about your project. Ask what changed since last week. Generate variations of a pattern. Find the version where the breakdown worked."),
      h("div", { class: "ai-grid" },
        AI_EXAMPLES.map((ex) => h("article", { class: "ai-card" }, [
          h("div", { class: "ai-card__head" }, [
            h("span", { class: "ai-card__badge" }, "CONCEPT"),
          ]),
          h("div", { class: "ai-msg ai-msg--user" }, [
            h("span", { class: "ai-msg__who" }, "you"),
            h("p", null, ex.user),
          ]),
          h("div", { class: "ai-msg ai-msg--bot" }, [
            h("span", { class: "ai-msg__who" }, "claude"),
            h("p", null, ex.ai),
          ]),
          h("p", { class: "ai-card__outcome" }, ex.outcome),
        ])),
      ),
      h("p", { class: "section-foot" }, "Currently in development. Early waitlist members get priority access."),
    ]),
  );
}

// ---- version control section ---------------------------------------

interface VcCard {
  title: string;
  body: (Node | string)[];
}

const VC_CARDS: VcCard[] = [
  {
    title: "Auto-snapshots",
    body: ["Save once, get versioned forever. dawhub captures your project state without manual commits or naming files ", h("code", null, "v7_FINAL"), "."],
  },
  {
    title: "Semantic diffs",
    body: [
      "See exactly what changed: ",
      h("em", null, "“4 notes added to drums, lead volume 78% → 85%, Serum preset switched.”"),
      " Not “binary files differ.”",
    ],
  },
  {
    title: "One-click rollback",
    body: ["Undo a single change without losing the rest. Roll back the mix without touching the arrangement."],
  },
];

function renderVcSection(): HTMLElement {
  return h("section", { class: "section--vc", id: "how" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "FOUNDATION"),
        h("h2", { class: "section-title" }, [
          "EVERY SAVE, TRACKED. EVERY CHANGE, ",
          h("span", { class: "underline" }, "VISIBLE"),
          ".",
        ]),
      ]),
      h("p", { class: "section-lead" }, "Behind every AI conversation is a complete history of your project. dawhub stores every version, computes structural diffs between any two saves, and shows you exactly what's different — channels, patterns, mixer state, plugin presets, automation."),
      h("div", { class: "vc-grid" },
        VC_CARDS.map((c, i) => h("div", { class: "vc-card" }, [
          h("div", { class: "vc-card__num" }, String(i + 1).padStart(2, "0")),
          h("h3", null, c.title),
          h("p", null, c.body),
        ])),
      ),
    ]),
  );
}

// ---- use cases -----------------------------------------------------

function renderUseCases(): HTMLElement {
  return h("section", { class: "scenarios", id: "use-cases" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "USE CASES" ),
        h("h2", { class: "section-title" }, [
          "BUILT FOR THE WAY ",
          h("span", { class: "underline" }, "PRODUCERS WORK"),
          ".",
        ]),
      ]),
      h("div", { class: "scenarios-grid" }, [
        h("div", { class: "scenario-card" }, [
          h("div", { class: "scenario-num" }, "01"),
          h("div", { class: "scenario-tag" }, "SOLO PRODUCERS"),
          h("h3", null, "Your own versions, finally readable."),
          h("p", null, "Come back to a project after a month off and have no idea what state you left it in. dawhub shows you the difference between any two saves — even ones from a year ago."),
          h("div", { class: "file-list" }, [
            h("div", null, [dim("/Projects/"), strong("track/")]),
            h("div", null, [dim("├── "), "track_v1.flp"]),
            h("div", null, [dim("├── "), "track_v2.flp"]),
            h("div", null, [dim("├── "), "track_v2_final.flp"]),
            h("div", null, [dim("└── "), h("span", { class: "accent-text" }, "track_v2_FINAL_real.flp")]),
          ]),
        ]),
        h("div", { class: "scenario-card" }, [
          h("div", { class: "scenario-num" }, "02"),
          h("div", { class: "scenario-tag" }, "COLLABORATORS"),
          h("h3", null, "Know what your collaborator touched."),
          h("p", null, "They send the FLP back with “added some drums, moved the breakdown.” Open it in dawhub and see exactly what they touched — every channel, pattern, plugin state."),
          h("div", { class: "file-list" }, [
            h("div", null, [dim("from: "), "alex@collabs.xyz"]),
            h("div", null, [dim("subject: "), "updated the drop"]),
            h("div", null, [dim("attached: "), strong("sunrise_v7.flp")]),
            h("div", { style: "margin-top: 4px;" }, h("span", { class: "accent-text" }, "→ 23 changes detected")),
          ]),
        ]),
      ]),
    ]),
  );
}

function dim(text: string): HTMLElement { return h("span", { class: "dim" }, text); }
function strong(text: string): HTMLElement { return h("span", { class: "strong" }, text); }

// ---- roadmap -------------------------------------------------------

interface RoadmapItem {
  status: "shipped" | "wip" | "planned";
  title: string;
  desc: string;
}

const ROADMAP: RoadmapItem[] = [
  { status: "shipped", title: "flpdiff CLI", desc: "Open-source diff tool. MIT-licensed, on npm." },
  { status: "shipped", title: "Web diff viewer", desc: "Drop two .flp files, see the diff in your browser." },
  { status: "wip",     title: "Cloud project storage", desc: "Versioned project history in dawhub, not your Downloads folder." },
  { status: "wip",     title: "AI assistant via MCP", desc: "Connect Claude or any MCP-compatible AI to your project history." },
  { status: "planned", title: "Collaboration features", desc: "Sharing, comments, async review on changes." },
  { status: "planned", title: "Ableton (.als) support", desc: "Same diff engine, Live projects." },
  { status: "planned", title: "Logic and Reaper", desc: "After Ableton lands." },
];

const ROADMAP_LABEL: Record<RoadmapItem["status"], string> = {
  shipped: "SHIPPED",
  wip: "IN PROGRESS",
  planned: "PLANNED",
};

function renderRoadmap(): HTMLElement {
  return h("section", { class: "section--roadmap", id: "roadmap" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "ROADMAP"),
        h("h2", { class: "section-title" }, [
          "WHAT'S ",
          h("span", { class: "underline" }, "COMING"),
          ".",
        ]),
      ]),
      h("ol", { class: "roadmap-list" },
        ROADMAP.map((item) => h("li", { class: `roadmap-item roadmap-item--${item.status}` }, [
          h("span", { class: "roadmap-item__status" }, ROADMAP_LABEL[item.status]),
          h("div", { class: "roadmap-item__body" }, [
            h("h3", null, item.title),
            h("p", null, item.desc),
          ]),
        ])),
      ),
    ]),
  );
}

// ---- social proof --------------------------------------------------

function renderSocialProof(): HTMLElement {
  return h("section", { class: "section--social" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "TRUSTED FOUNDATION"),
        h("h2", { class: "section-title" }, [
          "BUILT ON ",
          h("span", { class: "underline" }, "FLPDIFF"),
          ".",
        ]),
      ]),
      h("div", { class: "social-grid" }, [
        h("div", { class: "social-copy" }, [
          h("p", null, [
            h("code", null, "flpdiff"),
            " is our open-source CLI for diffing FL Studio projects. MIT-licensed, available on npm, and the same engine that powers dawhub. Try it free at any time.",
          ]),
          h("a", { href: "/flpdiff", class: "btn btn-primary" }, "Explore flpdiff →"),
        ]),
      ]),
    ]),
  );
}

// ---- final CTA -----------------------------------------------------

function renderFinalCta(): HTMLElement {
  return h("section", { class: "waitlist-section", id: "waitlist" },
    h("div", { class: "waitlist-inner" }, [
      h("h3", null, [
        "GET ",
        h("span", { class: "accent-box" }, "EARLY"),
        h("br"),
        "ACCESS.",
      ]),
      h("p", null, "Waitlist members get priority invites and lock in early-bird pricing when dawhub opens up."),
      renderWaitlistForm({ ariaLabelPrefix: "Final CTA" }),
      h("div", { class: "waitlist-meta" }, "NO SPAM · WE EMAIL WHEN THERE'S SOMETHING TO SHARE"),
    ]),
  );
}

// ---- FAQ -----------------------------------------------------------

interface FaqItem {
  q: string;
  a: (Node | string)[];
}

const FAQ: FaqItem[] = [
  {
    q: "When does dawhub launch?",
    a: ["Targeting Q3 2026 for early access. Waitlist members get priority invites."],
  },
  {
    q: "Will it work with FL Studio Web?",
    a: ["Yes. Same .flp format — anything FL Studio Web saves, dawhub can read."],
  },
  {
    q: "Is this a replacement for FL Studio's cloud sync?",
    a: ["No. Image-Line's cloud handles storage; dawhub layers on top — version history, structural diffs, AI integration, collaboration. The two work together."],
  },
  {
    q: "How does AI integration work?",
    a: ["Through the Model Context Protocol (MCP). Claude Desktop and other MCP-compatible AIs connect to dawhub and can read your project history, propose changes as diffs, and answer questions about what changed when."],
  },
  {
    q: "What does it cost?",
    a: ["Free tier for solo use. Paid tier (price TBD) for collaboration, more storage, and advanced AI features. Final pricing is visible before you sign up."],
  },
  {
    q: "Do my files leave my computer?",
    a: ["For cloud features, yes — files upload to dawhub with encryption at rest. The flpdiff CLI and web viewer are 100% local. A self-hosted option for the hub is on the roadmap."],
  },
  {
    q: "Will you support other DAWs?",
    a: ["FL Studio first. Ableton Live (.als) next. Logic and Reaper after that."],
  },
  {
    q: "Is there an open-source component?",
    a: [
      "Yes. ",
      h("code", null, "flpdiff"),
      " — the diff engine — is fully open source under MIT. The hub's commercial features (storage, collaboration, AI integration) are closed.",
    ],
  },
];

function renderFaq(): HTMLElement {
  return h("section", { class: "faq", id: "faq" },
    h("div", { class: "container" }, [
      h("div", { class: "section-head" }, [
        h("span", { class: "section-label" }, "FAQ"),
        h("h2", { class: "section-title" }, [
          "QUESTIONS ",
          h("span", { class: "dim" }, "YOU"),
          " ",
          h("span", { class: "underline" }, "MIGHT ASK"),
        ]),
      ]),
      h("div", { class: "faq-list" },
        FAQ.map((item) => h("details", { class: "faq-item" }, [
          h("summary", null, item.q),
          h("div", { class: "answer" }, item.a),
        ])),
      ),
    ]),
  );
}
